// src/pages/patient/PatientSettings.jsx
import React, { useEffect, useMemo, useState } from "react";

import { patientSettingsApi } from "../../services/patientSettings";
import { dateOfBirthError, earliestISO, todayISO } from "../../utils/dateOfBirth";

function classNames(...arr) {
  return arr.filter(Boolean).join(" ");
}
function getProfilePhotoUrl(settings) {
  return settings?.patient?.profile_photo_url || "";
}
function Icon({ name, className = "h-5 w-5" }) {
  switch (name) {
    case "settings":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path
            d="M12 15.5A3.5 3.5 0 1 0 12 8.5a3.5 3.5 0 0 0 0 7Z"
            stroke="currentColor"
            strokeWidth="2.1"
          />
          <path
            d="M19.4 15a1.8 1.8 0 0 0 .4 2l.1.1a2.1 2.1 0 1 1-3 3l-.1-.1a1.8 1.8 0 0 0-2-.4 1.8 1.8 0 0 0-1.1 1.7V21a2.1 2.1 0 1 1-4.2 0v-.2a1.8 1.8 0 0 0-1.1-1.7 1.8 1.8 0 0 0-2 .4l-.1.1a2.1 2.1 0 1 1-3-3l.1-.1a1.8 1.8 0 0 0 .4-2 1.8 1.8 0 0 0-1.7-1.1H3a2.1 2.1 0 1 1 0-4.2h.2a1.8 1.8 0 0 0 1.7-1.1 1.8 1.8 0 0 0-.4-2l-.1-.1a2.1 2.1 0 1 1 3-3l.1.1a1.8 1.8 0 0 0 2 .4h.1A1.8 1.8 0 0 0 10.6 3V3a2.1 2.1 0 1 1 4.2 0v.2a1.8 1.8 0 0 0 1.1 1.7 1.8 1.8 0 0 0 2-.4l.1-.1a2.1 2.1 0 1 1 3 3l-.1.1a1.8 1.8 0 0 0-.4 2v.1A1.8 1.8 0 0 0 21 10.6h0a2.1 2.1 0 1 1 0 4.2h-.2a1.8 1.8 0 0 0-1.4.2Z"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );

    case "user":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path
            d="M12 12a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9Z"
            stroke="currentColor"
            strokeWidth="2.1"
          />
          <path
            d="M4 21a8 8 0 0 1 16 0"
            stroke="currentColor"
            strokeWidth="2.1"
            strokeLinecap="round"
          />
        </svg>
      );

    case "lock":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path
            d="M7 10V8a5 5 0 0 1 10 0v2"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
          />
          <path
            d="M6 10h12a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2Z"
            stroke="currentColor"
            strokeWidth="2.2"
          />
        </svg>
      );

    case "mail":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path
            d="M4 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6Z"
            stroke="currentColor"
            strokeWidth="2.1"
          />
          <path
            d="m5.5 7.5 6.2 5a1 1 0 0 0 1.3 0l6.2-5"
            stroke="currentColor"
            strokeWidth="2.1"
            strokeLinecap="round"
          />
        </svg>
      );

    case "phone":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path
            d="M7 3h10a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z"
            stroke="currentColor"
            strokeWidth="2.1"
          />
          <path
            d="M10 18h4"
            stroke="currentColor"
            strokeWidth="2.1"
            strokeLinecap="round"
          />
        </svg>
      );

    case "shield":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path
            d="M12 3 20 6v6c0 5-3.4 8.3-8 9-4.6-.7-8-4-8-9V6l8-3Z"
            stroke="currentColor"
            strokeWidth="2.1"
            strokeLinejoin="round"
          />
          <path
            d="m8.8 12 2 2 4.6-5"
            stroke="currentColor"
            strokeWidth="2.1"
            strokeLinecap="round"
          />
        </svg>
      );

    case "check":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path
            d="M20 6 9 17l-5-5"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );

    case "eye":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path
            d="M2.5 12s3.5-7 9.5-7 9.5 7 9.5 7-3.5 7-9.5 7-9.5-7-9.5-7Z"
            stroke="currentColor"
            strokeWidth="2.1"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2.1" />
        </svg>
      );

    case "eyeOff":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path
            d="M10.6 5.2A10.8 10.8 0 0 1 12 5c6 0 9.5 7 9.5 7a18 18 0 0 1-2.2 3.3"
            stroke="currentColor"
            strokeWidth="2.1"
            strokeLinecap="round"
          />
          <path
            d="M6.3 6.3C3.7 8.2 2.5 12 2.5 12s3.5 7 9.5 7c1.7 0 3.2-.3 4.5-.9"
            stroke="currentColor"
            strokeWidth="2.1"
            strokeLinecap="round"
          />
          <path d="M3 3l18 18" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" />
        </svg>
      );

    case "calendar":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path
            d="M7 3v3M17 3v3M4 9h16M6 6h12a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2Z"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );

    case "refresh":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path
            d="M20 12a8 8 0 1 1-2.3-5.7M20 4v5h-5"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );

    default:
      return null;
  }
}

function Button({ children, className = "", ...props }) {
  return (
    <button
      {...props}
      className={classNames(
        "inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60",
        className
      )}
    >
      {children}
    </button>
  );
}

function PrimaryButton({ children, className = "", ...props }) {
  return (
    <button
      {...props}
      className={classNames(
        "inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-blue-600 via-sky-500 to-cyan-500 px-5 py-3 text-sm font-extrabold text-white shadow-[0_14px_30px_rgba(37,99,235,0.22)] transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0",
        className
      )}
    >
      {children}
    </button>
  );
}

function Pill({ children, tone = "neutral" }) {
  const map = {
    neutral: "border-slate-200 bg-slate-50 text-slate-700",
    success: "border-emerald-200 bg-emerald-50 text-emerald-700",
    warn: "border-amber-200 bg-amber-50 text-amber-700",
    danger: "border-rose-200 bg-rose-50 text-rose-700",
    info: "border-sky-200 bg-sky-50 text-sky-700",
    violet: "border-violet-200 bg-violet-50 text-violet-700",
  };

  return (
    <span className={classNames("inline-flex items-center rounded-full border px-3 py-1 text-xs font-bold", map[tone])}>
      {children}
    </span>
  );
}

function Panel({ title, subtitle, right, children }) {
  return (
    <div className="overflow-hidden rounded-[32px] border border-slate-200 bg-white shadow-soft">
      <div className="flex flex-col justify-between gap-4 border-b border-slate-100 bg-slate-50/80 px-6 py-5 md:flex-row md:items-start">
        <div>
          <div className="text-lg font-black text-slate-950">{title}</div>
          {subtitle ? <div className="mt-1 text-sm text-slate-500">{subtitle}</div> : null}
        </div>
        {right}
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

function Field({ label, icon, children, hint, error }) {
  return (
    <div>
      <label className="text-sm font-black text-slate-800">{label}</label>
      {hint ? <div className="mt-1 text-xs text-slate-500">{hint}</div> : null}

      <div className="relative mt-2">
        {icon ? (
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
            <Icon name={icon} className="h-5 w-5" />
          </span>
        ) : null}

        {children}
      </div>

      {error ? <div className="mt-1.5 text-xs font-bold text-rose-600">{error}</div> : null}
    </div>
  );
}

function InfoTile({ label, value, icon, tone = "info" }) {
  const toneClass =
    tone === "success"
      ? "bg-emerald-50 text-emerald-700 border-emerald-100"
      : tone === "warn"
      ? "bg-amber-50 text-amber-700 border-amber-100"
      : tone === "danger"
      ? "bg-rose-50 text-rose-700 border-rose-100"
      : "bg-sky-50 text-sky-700 border-sky-100";

  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-5">
      <div className="flex items-start gap-3">
        <div className={classNames("rounded-2xl border p-3", toneClass)}>
          <Icon name={icon} className="h-5 w-5" />
        </div>

        <div className="min-w-0">
          <div className="text-xs font-black uppercase tracking-wide text-slate-500">
            {label}
          </div>
          <div className="mt-1 break-words text-sm font-black text-slate-950">
            {value || "—"}
          </div>
        </div>
      </div>
    </div>
  );
}

function formatDateTime(value) {
  if (!value) return "—";

  try {
    return new Intl.DateTimeFormat("en-CA", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date(value));
  } catch {
    return value;
  }
}

function passwordScore(password) {
  if (!password) return 0;

  let score = 0;
  if (password.length >= 8) score += 1;
  if (password.length >= 12) score += 1;
  if (/[A-Z]/.test(password)) score += 1;
  if (/[0-9]/.test(password)) score += 1;
  if (/[^A-Za-z0-9]/.test(password)) score += 1;

  return Math.min(score, 5);
}

function passwordLabel(score) {
  if (score <= 1) return "Weak";
  if (score === 2) return "Fair";
  if (score === 3) return "Good";
  return "Strong";
}

export default function PatientSettings() {
  const [activeTab, setActiveTab] = useState("profile");
  const [photoUploading, setPhotoUploading] = useState(false);

  const [settings, setSettings] = useState(null);
  const [profileForm, setProfileForm] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    date_of_birth: "",
  });

  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });

  const [showPassword, setShowPassword] = useState({
    current: false,
    next: false,
    confirm: false,
  });

  const [loading, setLoading] = useState(true);
  const [profileSaving, setProfileSaving] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);

  const [error, setError] = useState("");
  const [profileSuccess, setProfileSuccess] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");

  const [fieldErrors, setFieldErrors] = useState({});

  const input =
    "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-500";
  const inputWithIcon = classNames(input, "pl-12");

  const score = useMemo(
    () => passwordScore(passwordForm.new_password),
    [passwordForm.new_password]
  );

  const fullName = useMemo(() => {
    const p = settings?.patient;
    if (!p) return "Patient";
    return `${p.first_name || ""} ${p.last_name || ""}`.trim() || "Patient";
  }, [settings]);

  useEffect(() => {
    loadSettings();
  }, []);

  async function loadSettings() {
    setLoading(true);
    setError("");

    try {
      const data = await patientSettingsApi.me();
      setSettings(data);

      setProfileForm({
        first_name: data.patient?.first_name || "",
        last_name: data.patient?.last_name || "",
        phone: data.account?.phone || "",
        // Services with an age limit cannot be booked without this.
        date_of_birth: data.patient?.date_of_birth || "",
      });
    } catch (e) {
      setError(e.message || "Failed to load settings.");
    } finally {
      setLoading(false);
    }
  }

  function updateProfileField(key, value) {
    setProfileForm((prev) => ({
      ...prev,
      [key]: value,
    }));

    setFieldErrors((prev) => ({
      ...prev,
      [key]: undefined,
    }));
    setError("");
    setProfileSuccess("");
  }

  function updatePasswordField(key, value) {
    setPasswordForm((prev) => ({
      ...prev,
      [key]: value,
    }));

    setFieldErrors((prev) => ({
      ...prev,
      [key]: undefined,
    }));
    setError("");
    setPasswordSuccess("");
  }

  async function handleProfilePhotoChange(e) {
  const file = e.target.files?.[0];
  if (!file) return;

  setError("");
  setProfileSuccess("");

  if (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(file.type)) {
    setError("Only JPG, PNG, and WEBP images are allowed.");
    e.target.value = "";
    return;
  }

  if (file.size > 5 * 1024 * 1024) {
    setError("Profile photo must be smaller than 5 MB.");
    e.target.value = "";
    return;
  }

  setPhotoUploading(true);

  try {
    const data = await patientSettingsApi.uploadProfilePhoto(file);
    setSettings(data);
    setProfileSuccess("Profile photo updated successfully.");
  } catch (e) {
    setError(e.message || "Failed to upload profile photo.");
  } finally {
    setPhotoUploading(false);
    e.target.value = "";
  }
}

async function handleRemoveProfilePhoto() {
  setError("");
  setProfileSuccess("");
  setPhotoUploading(true);

  try {
    const data = await patientSettingsApi.deleteProfilePhoto();
    setSettings(data);
    setProfileSuccess("Profile photo removed successfully.");
  } catch (e) {
    setError(e.message || "Failed to remove profile photo.");
  } finally {
    setPhotoUploading(false);
  }
}

  function validateProfile() {
    const next = {};

    if (!profileForm.first_name.trim()) {
      next.first_name = "First name is required.";
    }

    if (!profileForm.last_name.trim()) {
      next.last_name = "Last name is required.";
    }

    if (profileForm.phone.trim()) {
      const clean = profileForm.phone.replace(/\s+/g, "");
      if (!/^[0-9+()-]{8,20}$/.test(clean)) {
        next.phone = "Please enter a valid phone number.";
      }
    }

    // Optional, but if given it must be a real past date: age eligibility for
    // age-restricted services is decided from it.
    const dobProblem = dateOfBirthError(profileForm.date_of_birth, { required: false });
    if (dobProblem) next.date_of_birth = dobProblem;

    setFieldErrors(next);
    return Object.keys(next).length === 0;
  }

  function validatePassword() {
    const next = {};

    if (!passwordForm.current_password) {
      next.current_password = "Current password is required.";
    }

    if (!passwordForm.new_password) {
      next.new_password = "New password is required.";
    } else if (passwordForm.new_password.length < 8) {
      next.new_password = "New password must be at least 8 characters.";
    } else if (!/[A-Za-z]/.test(passwordForm.new_password) || !/[0-9]/.test(passwordForm.new_password)) {
      next.new_password = "Use at least one letter and one number.";
    }

    if (!passwordForm.confirm_password) {
      next.confirm_password = "Please confirm your new password.";
    } else if (passwordForm.new_password !== passwordForm.confirm_password) {
      next.confirm_password = "Passwords do not match.";
    }

    if (
      passwordForm.current_password &&
      passwordForm.new_password &&
      passwordForm.current_password === passwordForm.new_password
    ) {
      next.new_password = "New password cannot be the same as current password.";
    }

    setFieldErrors(next);
    return Object.keys(next).length === 0;
  }

  async function saveProfile(e) {
    e.preventDefault();

    if (!validateProfile()) return;

    setProfileSaving(true);
    setError("");
    setProfileSuccess("");

    try {
      const payload = {
        first_name: profileForm.first_name.trim(),
        last_name: profileForm.last_name.trim(),
        phone: profileForm.phone.trim() || null,
      };
      // Sent only when it actually changed: an unrelated save must not clear a
      // date of birth recorded while booking.
      const storedDob = settings?.patient?.date_of_birth || "";
      const nextDob = profileForm.date_of_birth || "";
      if (nextDob !== storedDob) payload.date_of_birth = nextDob || null;

      const data = await patientSettingsApi.updateProfile(payload);

      setSettings(data);
      setProfileSuccess("Profile updated successfully.");
    } catch (e) {
      setError(e.message || "Failed to update profile.");
    } finally {
      setProfileSaving(false);
    }
  }

  async function changePassword(e) {
    e.preventDefault();

    if (!validatePassword()) return;

    setPasswordSaving(true);
    setError("");
    setPasswordSuccess("");

    try {
      const res = await patientSettingsApi.changePassword({
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      });

      setPasswordSuccess(res.message || "Password changed successfully.");
      setPasswordForm({
        current_password: "",
        new_password: "",
        confirm_password: "",
      });
    } catch (e) {
      setError(e.message || "Failed to change password.");
    } finally {
      setPasswordSaving(false);
    }
  }

  if (loading) {
    return (
      <main className="min-h-screen bg-slate-50 p-6">
        <div className="mx-auto max-w-7xl">
          <div className="rounded-[32px] border border-slate-200 bg-white p-12 text-center shadow-soft">
            <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-slate-200 border-t-blue-600" />
            <div className="mt-4 text-sm font-bold text-slate-500">
              Loading settings...
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-[1500px] px-4 py-6 lg:px-6">
        <div
          className="overflow-hidden rounded-[36px] border border-white/80 bg-white shadow-[0_24px_80px_rgba(15,23,42,0.10)]"
          style={{
            background:
              "linear-gradient(135deg, rgba(37,99,235,0.10), rgba(14,165,233,0.08), rgba(255,255,255,0.96))",
          }}
        >
          {/* <div className="relative overflow-hidden p-6 lg:p-8">
            <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-cyan-300/30 blur-3xl" />
            <div className="absolute -bottom-24 left-24 h-72 w-72 rounded-full bg-blue-500/20 blur-3xl" />

            <div className="relative z-10 flex flex-col justify-between gap-6 lg:flex-row lg:items-center">
              <div className="flex items-center gap-5">
                <div className="flex h-20 w-20 items-center justify-center rounded-[28px] bg-gradient-to-r from-blue-600 via-sky-500 to-cyan-500 text-white shadow-[0_18px_45px_rgba(37,99,235,0.26)]">
                  <Icon name="settings" className="h-10 w-10" />
                </div>

                <div>
                  <div className="inline-flex items-center gap-2 rounded-full border border-sky-100 bg-sky-50 px-3 py-1 text-xs font-black text-sky-700">
                    ThePulseCare Settings
                  </div>
                  <h1 className="mt-3 text-4xl font-black tracking-tight text-slate-950">
                    Account settings
                  </h1>
                  <p className="mt-2 text-sm text-slate-500">
                    Manage your profile, contact details, and password securely.
                  </p>
                </div>
              </div>

              <div className="rounded-[28px] border border-white/70 bg-white/80 p-5 shadow-soft backdrop-blur-xl">
                <div className="flex items-center gap-4">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-900 text-white">
                    {fullName
                      .split(" ")
                      .map((x) => x[0])
                      .join("")
                      .slice(0, 2)
                      .toUpperCase()}
                  </div>

                  <div>
                    <div className="text-lg font-black text-slate-950">
                      {fullName}
                    </div>
                    <div className="mt-1 text-sm text-slate-500">
                      {settings?.account?.email}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {settings?.account?.email_verified ? (
                        <Pill tone="success">Verified email</Pill>
                      ) : (
                        <Pill tone="warn">Email not verified</Pill>
                      )}
                      <Pill tone="info">{settings?.account?.status}</Pill>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div> */}

          <div className="border-t border-white/70 bg-white/70 px-6 py-4 lg:px-8">
            <div className="flex flex-wrap gap-2">
              {[
                { key: "profile", label: "Profile", icon: "user" },
                { key: "security", label: "Security", icon: "lock" },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => {
                    setActiveTab(tab.key);
                    setError("");
                    setProfileSuccess("");
                    setPasswordSuccess("");
                    setFieldErrors({});
                  }}
                  className={classNames(
                    "inline-flex items-center gap-2 rounded-2xl px-4 py-2.5 text-sm font-black transition",
                    activeTab === tab.key
                      ? "bg-blue-600 text-white shadow-[0_12px_28px_rgba(37,99,235,0.24)]"
                      : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                  )}
                >
                  <Icon name={tab.icon} className="h-4 w-4" />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {error ? (
          <div className="mt-5 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm font-bold text-rose-700">
            {error}
          </div>
        ) : null}

        <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-12">
          <div className="xl:col-span-4">
            <Panel
              title="Account overview"
              subtitle="Your current ThePulseCare account details."
              right={
                <Button onClick={loadSettings}>
                  <Icon name="refresh" className="h-4 w-4" />
                  Refresh
                </Button>
              }
            >
              <div className="space-y-4">
                <InfoTile
                  label="Email"
                  value={settings?.account?.email}
                  icon="mail"
                  tone={settings?.account?.email_verified ? "success" : "warn"}
                />
                <InfoTile
                  label="Phone"
                  value={settings?.account?.phone || "Not added"}
                  icon="phone"
                  tone="info"
                />
                <InfoTile
                  label="Account status"
                  value={settings?.account?.status}
                  icon="shield"
                  tone="success"
                />
                {/* <InfoTile
                  label="Last login"
                  value={formatDateTime(settings?.account?.last_login_at)}
                  icon="lock"
                  tone="violet"
                /> */}
              </div>
            </Panel>
          </div>

          <div className="xl:col-span-8">
            {activeTab === "profile" ? (
              <Panel
                title="Profile details"
                subtitle="Update your patient profile and contact information."
              >
                {profileSuccess ? (
                  <div className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm font-bold text-emerald-700">
                    {profileSuccess}
                  </div>
                ) : null}
                <div className="mb-6 rounded-[28px] border border-slate-200 bg-slate-50 p-5">
  <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
    <div className="flex items-center gap-4">
      <div className="relative h-24 w-24 overflow-hidden rounded-[28px] bg-gradient-to-r from-blue-600 via-sky-500 to-cyan-500 text-white shadow-soft">
        {getProfilePhotoUrl(settings) ? (
          <img
            src={getProfilePhotoUrl(settings)}
            alt={fullName}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-2xl font-black">
            {fullName
              .split(" ")
              .map((x) => x[0])
              .join("")
              .slice(0, 2)
              .toUpperCase()}
          </div>
        )}
      </div>

      <div>
        <div className="text-lg font-black text-slate-950">
          Profile photo
        </div>
        <div className="mt-1 max-w-sm text-sm leading-6 text-slate-500">
          Upload a clear photo so clinics and doctors can identify your profile easily.
        </div>
        <div className="mt-2 text-xs font-semibold text-slate-400">
          JPG, PNG, or WEBP. Max 5 MB.
        </div>
      </div>
    </div>

    <div className="flex flex-wrap gap-2">
      <label className="inline-flex cursor-pointer items-center justify-center rounded-2xl bg-gradient-to-r from-blue-600 via-sky-500 to-cyan-500 px-4 py-2.5 text-sm font-extrabold text-white shadow-[0_14px_30px_rgba(37,99,235,0.22)] transition hover:-translate-y-0.5">
        {photoUploading ? "Uploading..." : "Upload photo"}
        <input
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp"
          className="hidden"
          onChange={handleProfilePhotoChange}
          disabled={photoUploading}
        />
      </label>

      {settings?.patient?.profile_photo_key ? (
        <button
          type="button"
          onClick={handleRemoveProfilePhoto}
          disabled={photoUploading}
          className="inline-flex items-center justify-center rounded-2xl border border-rose-200 bg-white px-4 py-2.5 text-sm font-bold text-rose-600 transition hover:bg-rose-50 disabled:opacity-60"
        >
          Remove
        </button>
      ) : null}
    </div>
  </div>
</div>

                <form onSubmit={saveProfile} className="space-y-5">
                  <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                    
                    <Field
                      label="First name"
                      icon="user"
                      error={fieldErrors.first_name}
                    >
                      <input
                        value={profileForm.first_name}
                        onChange={(e) => updateProfileField("first_name", e.target.value)}
                        className={classNames(
                          inputWithIcon,
                          fieldErrors.first_name &&
                            "border-rose-300 focus:border-rose-400 focus:ring-rose-100"
                        )}
                        placeholder="First name"
                        disabled={true}
                      />
                    </Field>

                    <Field
                      label="Last name"
                      icon="user"
                      error={fieldErrors.last_name}
                    >
                      <input
                        value={profileForm.last_name}
                        onChange={(e) => updateProfileField("last_name", e.target.value)}
                        className={classNames(
                          inputWithIcon,
                          fieldErrors.last_name &&
                            "border-rose-300 focus:border-rose-400 focus:ring-rose-100"
                        )}
                        placeholder="Last name"
                        disabled={true}
                      />
                    </Field>
                  </div>

                  <Field
                    label="Date of birth"
                    icon="calendar"
                    hint="Needed only for services with an age requirement. Used to work out your age; nothing else."
                    error={fieldErrors.date_of_birth}
                  >
                    <input
                      type="date"
                      value={profileForm.date_of_birth}
                      max={todayISO()}
                      min={earliestISO()}
                      onChange={(e) => updateProfileField("date_of_birth", e.target.value)}
                      className={classNames(
                        inputWithIcon,
                        fieldErrors.date_of_birth &&
                          "border-rose-300 focus:border-rose-400 focus:ring-rose-100"
                      )}
                      disabled={profileSaving}
                      data-testid="settings-date-of-birth"
                    />
                  </Field>

                  <Field
                    label="Phone number"
                    icon="phone"
                    hint="Used for appointment reminders and important clinic updates."
                    error={fieldErrors.phone}
                  >
                    <input
                      value={profileForm.phone}
                      onChange={(e) => updateProfileField("phone", e.target.value)}
                      className={classNames(
                        inputWithIcon,
                        fieldErrors.phone &&
                          "border-rose-300 focus:border-rose-400 focus:ring-rose-100"
                      )}
                      placeholder="+1 555 123 4567"
                      disabled={true}
                    />
                  </Field>

                  <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                    <InfoTile
                      label="Patient ID"
                      value={settings?.patient?.id}
                      icon="user"
                      tone="neutral"
                    />
                    <InfoTile
                      label="Account created"
                      value={formatDateTime(settings?.account?.created_at)}
                      icon="check"
                      tone="success"
                    />
                  </div>

                  <div className="flex justify-end">
                    <PrimaryButton type="submit" disabled={profileSaving}>
                      {profileSaving ? "Saving profile..." : "Save profile"}
                    </PrimaryButton>
                  </div>
                </form>
              </Panel>
            ) : null}

            {activeTab === "security" ? (
              <Panel
                title="Change password"
                subtitle="Use a strong password to protect your account."
              >
                {passwordSuccess ? (
                  <div className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm font-bold text-emerald-700">
                    {passwordSuccess}
                  </div>
                ) : null}

                <form onSubmit={changePassword} className="space-y-5">
                  <PasswordField
                    label="Current password"
                    value={passwordForm.current_password}
                    onChange={(v) => updatePasswordField("current_password", v)}
                    visible={showPassword.current}
                    onToggle={() =>
                      setShowPassword((p) => ({ ...p, current: !p.current }))
                    }
                    error={fieldErrors.current_password}
                    disabled={passwordSaving}
                  />

                  <PasswordField
                    label="New password"
                    value={passwordForm.new_password}
                    onChange={(v) => updatePasswordField("new_password", v)}
                    visible={showPassword.next}
                    onToggle={() =>
                      setShowPassword((p) => ({ ...p, next: !p.next }))
                    }
                    error={fieldErrors.new_password}
                    disabled={passwordSaving}
                  />

                  {passwordForm.new_password ? (
                    <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
                      <div className="mb-3 flex items-center justify-between">
                        <div className="text-sm font-black text-slate-800">
                          Password strength
                        </div>
                        <Pill tone={score >= 4 ? "success" : score >= 3 ? "info" : score >= 2 ? "warn" : "danger"}>
                          {passwordLabel(score)}
                        </Pill>
                      </div>

                      <div className="grid grid-cols-5 gap-2">
                        {[1, 2, 3, 4, 5].map((x) => (
                          <div
                            key={x}
                            className={classNames(
                              "h-2 rounded-full transition",
                              x <= score
                                ? score >= 4
                                  ? "bg-emerald-500"
                                  : score >= 3
                                  ? "bg-sky-500"
                                  : score >= 2
                                  ? "bg-amber-500"
                                  : "bg-rose-500"
                                : "bg-slate-200"
                            )}
                          />
                        ))}
                      </div>

                      <div className="mt-3 text-xs leading-5 text-slate-500">
                        Recommended: 12+ characters, uppercase letter, number, and symbol.
                      </div>
                    </div>
                  ) : null}

                  <PasswordField
                    label="Confirm new password"
                    value={passwordForm.confirm_password}
                    onChange={(v) => updatePasswordField("confirm_password", v)}
                    visible={showPassword.confirm}
                    onToggle={() =>
                      setShowPassword((p) => ({ ...p, confirm: !p.confirm }))
                    }
                    error={fieldErrors.confirm_password}
                    disabled={passwordSaving}
                  />

                  <div className="rounded-3xl border border-sky-100 bg-sky-50 p-5">
                    <div className="flex gap-3">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white text-sky-700">
                        <Icon name="shield" className="h-5 w-5" />
                      </div>
                      <div>
                        <div className="text-sm font-black text-sky-900">
                          Security note
                        </div>
                        <div className="mt-1 text-sm leading-6 text-sky-800">
                          After changing your password, use the new password the next time you sign in.
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <PrimaryButton type="submit" disabled={passwordSaving}>
                      {passwordSaving ? "Changing password..." : "Change password"}
                    </PrimaryButton>
                  </div>
                </form>
              </Panel>
            ) : null}
          </div>
        </div>
      </div>
    </main>
  );
}

function PasswordField({
  label,
  value,
  onChange,
  visible,
  onToggle,
  error,
  disabled,
}) {
  const input =
    "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3.5 pl-12 pr-14 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-500";

  return (
    <Field label={label} icon="lock" error={error}>
      <input
        type={visible ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={classNames(
          input,
          error && "border-rose-300 focus:border-rose-400 focus:ring-rose-100"
        )}
        placeholder={label}
        disabled={disabled}
      />

      <button
        type="button"
        onClick={onToggle}
        disabled={disabled}
        className="absolute right-3 top-1/2 -translate-y-1/2 rounded-xl p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 disabled:opacity-60"
      >
        <Icon name={visible ? "eyeOff" : "eye"} className="h-5 w-5" />
      </button>
    </Field>
  );
}